'use client';

import {
  useEffect, useRef, useState, useCallback,
} from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import useSWR from 'swr';

import { Account } from '@/features/dashboard/interface';
import { useAuthStore, UserAccount } from '@/stores/userStore';

import { Amounts } from '@/interfaces/core';

import { useAuth } from '@/core/auth/client/AuthProvider';

const fetcher = async (...args: Parameters<typeof fetch>) => {
  const res = await fetch(...args);
  return res.json();
};

export const useBalanceOptimized = () => {
  const [prevAmount, setPrevAmount] = useState<number>(0);
  const [refreshInterval, setRefreshInterval] = useState<number>(30000);
  const [isUserIdle, setIsUserIdle] = useState<boolean>(false);
  const [localAmounts, setLocalAmounts] = useState<Amounts[] | null>(null);

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { session, signOut } = useAuth();
  const {
    balance, setBalance, clearStore, addAccount, setVouchers, account, setAccount,
  } = useAuthStore();

  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (!isMountedRef.current) return;

    setIsUserIdle(false);
    idleTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setIsUserIdle(true);
      }
    }, 3 * 60 * 1000);
  }, []);

  const handleUserActivity = useCallback(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  // Idle timer setup
  useEffect(() => {
    window.addEventListener('mousemove', handleUserActivity, { passive: true });
    window.addEventListener('keypress', handleUserActivity, { passive: true });

    resetIdleTimer();

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keypress', handleUserActivity);
    };
  }, [handleUserActivity, resetIdleTimer]);

  // Route-based refresh interval logic
  useEffect(() => {
    if (typeof pathname === 'string' && pathname !== null) {
      if (pathname.startsWith('/casino/play')) {
        setRefreshInterval(10000);
      } else {
        setRefreshInterval(30000);
      }
    } else if (typeof window !== 'undefined') {
      setRefreshInterval(30000);
    } else if (searchParams && searchParams.get('deposit') === 'complete' && searchParams.get('pasarela') === 'payphone') {
      setRefreshInterval(4000);
    }
  }, [pathname, searchParams]);

  const { data, error, mutate } = useSWR(
    session?.user?.id ? `/api/balance?ref=${session.user.id}` : null,
    fetcher,
    {
      refreshInterval: isUserIdle ? 0 : refreshInterval,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 10000,
      keepPreviousData: true,
      onSuccess: async (responseData) => {
        try {
          if (responseData && responseData.success === false && responseData.error === 'unauthorized') {
            clearStore();
            signOut();
            return;
          }

          if (responseData?.data && responseData.balance && responseData.balance.length > 0) {
            const updatedAccount: UserAccount = {
              ...account,
              ...responseData.data,
              kycLevel: responseData.kyc_level ?? account.kycLevel ?? 0,
              kycStatus: responseData.kyc_status ?? account.kycStatus ?? 0,
            };

            setLocalAmounts(responseData.balance);

            setBalance(responseData.balance);
            setAccount(updatedAccount);
          }

          if (responseData?.wallets) {
            addAccount(responseData.wallets as Account[]);
          }

          if (responseData?.vouchers !== undefined) {
            setVouchers(responseData.vouchers);
          }
        } catch (apiError) {
          console.warn('Balance API error:', apiError);
        }
      },
      onError: (apiError) => {
        console.warn('Balance API request failed:', apiError);
      },
    },
  );

  // [] is truthy, so a store wiped by clearStore must not hide the SWR cache.
  // A remount within dedupingInterval does not call onSuccess again.
  const fetchedBalance =
    Array.isArray(data?.balance) && data.balance.length > 0 ? data.balance : null
  const storedBalance = balance?.length > 0 ? balance : null
  const currentBalance = localAmounts || fetchedBalance || storedBalance || []
  const currentAmount = currentBalance?.[0]?.amount ?? 0

  useEffect(() => {
    if (currentAmount !== prevAmount && currentAmount > 0) {
      setPrevAmount(currentAmount);
    }
  }, [currentAmount, prevAmount]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, []);

  const refreshBalance = useCallback(() => {
    mutate();
  }, [mutate]);

  return {
    // Data
    balance: currentBalance,
    prevAmount,
    account,

    isLoading: !data && !error,
    isError: !!error,

    // User activity
    isUserIdle,

    refreshBalance,

    refreshInterval,
  };
};
